import { useId, forwardRef, useMemo } from "react"
import { Layout } from "./layout.js"
import { typography, colors } from "design"
import { classes } from "utils"
import { useFormContext, Controller } from "react-hook-form"

export const checkboxVariablesLight = {
  "--resbird-checkbox-border-color": colors.primaryBlueGrey,
  "--resbird-checkbox-color": colors.primaryBlueGrey,
  "--resbird-checkbox-label-color": colors.lightGrey100,
  "--resbird-checkbox-error-color": colors.red,
  "--resbird-checkbox-disabled-background": colors.grey200,
}

const computeCheckboxStyle = ({ error, disabled }) => ({
  height: 18,
  width: 18,
  border: "1px solid",
  borderRadius: 4,
  display: "inline-block",
  marginRight: 10,
  verticalAlign: "middle",
  cursor: disabled ? "not-allowed" : "pointer",
  borderColor: error
    ? "var(--resbird-checkbox-error-color)"
    : "var(--resbird-checkbox-border-color)",
  backgroundColor: "#FFFFFF", // White background for the checkbox
  accentColor: colors.primaryBlueGrey,
})

/**
 * Checkbox component with customizable styles and error handling.
 *
 * This component renders a styled checkbox with an optional label and error message.
 * The styles are derived from a design system and can be customized via props.
 * It uses CSS variables for theming and supports error states with appropriate styling.
 *
 * @param {Object} props - The props for the Checkbox component.
 * @param {string} [props.error] - The error, if present.
 * @param {string} [props.label] - The label text for the checkbox.
 * @param {Object} [props.labelStyle] - Custom styles for the label.
 * @param {Object} [props.checkboxStyle] - Custom styles for the checkbox.
 * @param {Object} [props.errorStyle] - Custom styles for the error message.
 * @param {React.Ref} ref - The ref to be forwarded to the checkbox element.
 *
 * @returns {JSX.Element} The rendered Checkbox component.
 *
 * @example
 *
 * import { Checkbox } from './Checkbox.js';
 *
 * const MyComponent = () => (
 *   <Checkbox
 *     label="Accept Terms"
 *     error="You must accept the terms"
 *     labelStyle={{ fontWeight: 'bold' }}
 *     checkboxStyle={{ borderColor: 'blue' }}
 *     errorStyle={{ color: 'red' }}
 *   />
 * );
 *
 * export default MyComponent;
 */
export const Checkbox = forwardRef(
  (
    {
      error: propsError,
      label,
      fieldName = "",
      rules = {},
      className,
      labelStyle,
      errorStyle,
      checkboxStyle: propsCheckboxStyle,
      disabled,
      ...props
    },
    propsRef,
  ) => {
    const id = useId()
    const errorId = useId()

    const { formState, control } = useFormContext() ?? {}

    const error = useMemo(
      () =>
        !_.isEmpty(rules)
          ? propsError || _.get(formState.errors, fieldName)?.message
          : null,
      [fieldName, formState, propsError, rules],
    )

    const checkboxStyle = useMemo(
      () => computeCheckboxStyle({ error, disabled }),
      [error, disabled],
    )

    return (
      <Controller
        control={control}
        name={fieldName}
        rules={rules}
        render={({ field: { onChange, onBlur, value, ref } }) => (
          <Layout
            className={classes("checkbox-container", className)}
            css={{
              display: "flex",
              alignItems: "center",
              cursor: disabled ? "not-allowed" : "pointer",
            }}
            ref={ref}
            role="checkbox"
            aria-checked={!!value}
            aria-describedby={error ? errorId : undefined}
          >
            <input
              type="checkbox"
              className="checkbox"
              css={[checkboxStyle, propsCheckboxStyle]}
              disabled={disabled}
              onChange={disabled ? null : onChange}
              onBlur={onBlur}
              checked={value || false}
              ref={propsRef || ref}
              {...{ id, ...props }}
            />
            {label && (
              <label
                className="checkbox-label"
                css={[
                  {
                    marginLeft: 10,
                    color: "var(--resbird-checkbox-label-color)",
                    cursor: disabled ? "not-allowed" : "pointer",
                  },
                  typography.widgetLabel,
                  labelStyle,
                ]}
                htmlFor={id}
              >
                {label}
              </label>
            )}
            {error && (
              <label
                className="error-description"
                css={[
                  errorStyle,
                  {
                    marginTop: 5,
                    fontSize: "0.87rem",
                    color: "var(--resbird-checkbox-error-color)",
                  },
                ]}
                htmlFor={id}
                id={errorId}
                role="aria-errormessage"
              >
                {error}
              </label>
            )}
          </Layout>
        )}
      />
    )
  },
)

Checkbox.displayName = "Checkbox"
